import type { IAggFunc, IAggFuncParams } from 'ag-grid-community';
import { toBig } from '../../../utils';
import type { SizeValue } from './types';

const EMPTY_NO_AGG_RESULT: SizeValue = { value: '', currency: '', aggregate: false };

/** Stablecoins pegged to USD - treated as equivalent for aggregation */
const USD_FAMILY_CURRENCIES = new Set(['USD', 'USDT', 'USDC']);

function isUsdFamilyCurrency(currency: string | undefined): boolean {
  return currency != null && USD_FAMILY_CURRENCIES.has(currency);
}

/**
 * Finds the maximum SizeValue among child rows.
 *
 * **Restrictive use case**: Requires compatible currencies. USD/USDT/USDC are treated
 * as equivalent. Returns empty non-aggregatable result if currencies don't align.
 */
export const maxSizeAggFunc: IAggFunc<unknown, SizeValue> = ({ values }: IAggFuncParams<unknown, SizeValue>) => {
  if (values.some(v => v?.aggregate === false)) {
    return EMPTY_NO_AGG_RESULT;
  }
  // Filter to only valid numeric values (skip null, empty, or invalid BigSource)
  const validValues = values.filter((v): v is SizeValue => {
    if (v == null || v.value == null || v.value === '') {
      return false;
    }
    return toBig(v.value) != null;
  });
  // If no valid values exist, return aggregatable empty result (allows parent aggregation)
  if (validValues.length === 0) {
    return { value: '', currency: '', aggregate: true };
  }

  const firstCurrency = validValues[0].currency;
  const firstIsUsdFamily = isUsdFamilyCurrency(firstCurrency);
  for (const v of validValues) {
    const currenciesMatch = v.currency === firstCurrency;
    const bothUsdFamily = firstIsUsdFamily && isUsdFamilyCurrency(v.currency);
    if (!currenciesMatch && !bothUsdFamily) {
      return EMPTY_NO_AGG_RESULT;
    }
  }

  const max = validValues.reduce((acc, v) => {
    const accBig = toBig(acc.value);
    const vBig = toBig(v.value);
    if (!accBig || !vBig) {
      return acc;
    }
    return vBig.gt(accBig) ? v : acc;
  }, validValues[0]);

  return { ...max, aggregate: true };
};
